// Calendar store — view-signature-keyed cache for the dashboard
// calendar's loaded data (Stemne events, external events, all-day
// banners, working windows, blockers, slot mask).
//
// Why: switching between Dag/Uke/Måned on the same anchor used to
// refetch the whole range each time. The data is stable on a 30-
// second scale (other tabs / colleagues' edits aside), so caching
// by view signature turns view switches into reads from memory.
//
// Why module-level state (not Pinia): the project doesn't use
// Pinia; existing shared state lives in composables (useToast,
// useTenant, useCurrentAdmin) with module-scoped reactive refs.
// A Map<viewSignature, CachedView> with TTL achieves the same
// shape with less ceremony.
//
// Cache key shape:
//   "week:YYYY-MM-DD"  — Monday of the week
//   "month:YYYY-MM-DD" — first of the month
//   "day:YYYY-MM-DD"   — the displayed day
// The view passes its own (mode, anchor) → key derivation; this
// composable just keys on the resulting string.
//
// Freshness model:
//   1. In-memory map — survives within a session, hit immediately
//      on view-toggles and per-tab navigation. Cleared by
//      invalidateAll on local mutation.
//   2. localStorage backing store — persists across refreshes and
//      cold loads. Up to LS_MAX_ENTRIES recent (mode, anchor)
//      bundles per tenant, LRU-evicted. Schema-versioned so a
//      shape change in Event / CachedView doesn't crash on hot
//      reload (mismatched payloads are silently dropped).
//   3. Staleness contract — getCachedView returns the entry plus
//      its age in ms; the caller (loadWeek) decides what to do.
//      <30s = fresh, render straight. 30s..10min = stale-but-
//      usable, render and revalidate in background. >10min =
//      drop, fetch fresh only.
//   4. WebSocket patches — `calendar.booking.*` topics mutate
//      events.value AND write back through the cache, so a
//      colleague's edit lands in localStorage and a refresh sees
//      the latest state immediately.
//   5. Cross-tab sync — `storage` events fire on writes from
//      another tab; consumers subscribe via onCrossTabUpdate so
//      tab A's refresh-trigger reaches tab B without polling.
//
// Tenant scoping — the LS key includes the org slug (read once
// via getTenantSlug). Switching tenants means different LS
// namespaces; no chance of one tenant's bookings bleeding into
// another's cache.

// CachedView is the full ready-to-render bundle for one (mode,
// anchor) combination. Types are kept loose (`unknown[]`) so this
// module doesn't import-cycle through the calendar view's local
// interfaces; the view round-trips its own shapes through the
// cache unchanged. The view casts back to its concrete types on
// read.
//
// What's cached:
//   - `events`: post-merge array of Stemne + adapted external
//     events with `absDate` already attached. Directly assignable
//     to events.value on cache hit.
//   - `allDayBanners`: post-adaptation banners (one per day-event).
//   - `workingWindows`: operator's working_windows from the slot
//     endpoint, pass-through.
//   - `unbookableMaskKeys`: the slot mask serialized as the keys
//     array. Reconstructed via `new Set(keys)` on read so the
//     consumer can use Set semantics; storing the Set directly
//     would also work for the in-memory case, but keys are
//     structurally cloneable and friendlier if we ever persist
//     elsewhere (sessionStorage, devtools introspection).
//   - `blockers`: tenant-wide blockers from /api/admin/blockers.
//
// What's NOT cached (and why):
//   - `connections`: tenant-config-shaped, refetched on every
//     loadWeek. Cheap; orthogonal to the displayed range.
//   - `firstServiceId`: lazy-loaded once per session at the
//     module level, refresh-resistant by design.
//   - The pre-adaptation external events: the view's adapter
//     functions run weekStart-aware math, but on a cache hit
//     weekStart is the same as on cache write, so re-running them
//     is wasted work — we cache the post-adaptation result
//     directly.
import { ref } from 'vue'
import { getTenantSlug } from '@/utils/apiClient'

// The cache itself is a plain Map (deliberately non-reactive — view
// data flows through explicit loadWeek calls). This counter is the
// single reactive escape hatch: it bumps on every cache write /
// invalidation so computeds that read the cache synchronously (the
// month-carousel neighbour panes) re-run when a prefetch lands,
// without resorting to watchers (forbidden in this project).
export const calendarCacheVersion = ref(0)

export interface CachedView {
  events: unknown[]
  allDayBanners: unknown[]
  workingWindows: unknown[]
  unbookableMaskKeys: string[]
  blockers: unknown[]
  fetchedAt: number // Date.now() at write
}

// "Fresh" window — under this age, the cache entry can be used
// without revalidation. Matches the original 30s TTL.
export const FRESH_MS = 30_000

// "Stale-but-usable" window — beyond FRESH_MS but within
// STALE_MS, the entry is returned for instant first-paint AND
// the caller revalidates in the background. Beyond STALE_MS the
// entry is dropped; a fresh fetch is required.
//
// 10 min picks a balance between snappy refresh-restoration (a
// minute later, the calendar still feels instant) and risk of
// showing seriously stale data (a meeting cancelled an hour ago
// shouldn't still be on screen until the user touches something).
export const STALE_MS = 10 * 60 * 1000

// Bump when CachedView shape changes incompatibly. Old LS payloads
// at the previous version are silently dropped on read so refresh
// can't crash on a schema mismatch.
const CACHE_VERSION = 1

// Per-tenant LS slot. One JSON blob holds the full cache, indexed
// by viewKey, with an LRU list. Single read / write per call
// keeps the `storage` event simple (one key to watch from other
// tabs) and avoids walking localStorage to find related entries.
const LS_KEY_PREFIX = 'stemne:cal:cache:'
const LS_MAX_ENTRIES = 8

interface PersistedCache {
  version: number
  entries: Record<string, CachedView>
  // LRU order, MOST RECENTLY USED first. On write, the touched
  // key moves to the front; entries past LS_MAX_ENTRIES are
  // dropped.
  lru: string[]
}

const cache = new Map<string, CachedView>()

function lsKey(): string | null {
  const slug = getTenantSlug()
  if (!slug) return null
  return `${LS_KEY_PREFIX}${slug}`
}

function readPersisted(): PersistedCache | null {
  const key = lsKey()
  if (!key || typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedCache
    if (parsed?.version !== CACHE_VERSION) return null
    if (!parsed.entries || !parsed.lru) return null
    return parsed
  } catch {
    // Malformed JSON, blocked storage, quota error on read (rare).
    // Treat as cache miss — the network fetch will run.
    return null
  }
}

function writePersisted(blob: PersistedCache): void {
  const key = lsKey()
  if (!key || typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(key, JSON.stringify(blob))
  } catch {
    // QuotaExceededError / private mode / iframe sandbox — silently
    // skip persistence. The in-memory cache still works.
  }
}

// Reify a CachedView from the persisted form. fetchedAt comes
// across as a plain number — no transformation needed; the
// `events` etc. arrays are read back as-is (Date instances inside
// them survive JSON round-trip as ISO strings; this matters for
// `absDate` on Event objects — the calendar's eventOnDate helper
// only reads via toDateString(new Date(absDate)), but the type says
// Date. Fix-up happens at the view boundary if needed; until a
// real bug surfaces, the loose unknown[] typing here means it's
// the caller's job).
function hydrateFromLS(key: string): CachedView | null {
  const blob = readPersisted()
  if (!blob) return null
  const entry = blob.entries[key]
  if (!entry) return null
  // Restore Date objects on absDate — survives JSON round-trip as
  // ISO strings otherwise, which breaks toDateString (calls
  // .getFullYear on a string and throws TypeError). Applies to both
  // events and allDayBanners; both feed into eventOnDate when
  // rendering the calendar grid.
  const events = (entry.events as Array<{ absDate?: unknown }>).map(rehydrateAbsDate)
  const allDayBanners = (entry.allDayBanners as Array<{ absDate?: unknown }>).map(rehydrateAbsDate)
  return { ...entry, events, allDayBanners }
}

function rehydrateAbsDate<T extends { absDate?: unknown }>(item: T): T {
  if (typeof item.absDate === 'string') {
    return { ...item, absDate: new Date(item.absDate) }
  }
  return item
}

// Build the cache key for a (mode, anchor) combination. Anchor is
// rendered as YYYY-MM-DD in local time — same convention the rest
// of the calendar uses for date-anchored API params.
export function viewKey(mode: string, anchorYmd: string): string {
  return `${mode}:${anchorYmd}`
}

// Return the cached entry plus its age in ms. Null only when no
// entry exists OR the entry is past STALE_MS. Callers decide
// what to do based on age: render straight if < FRESH_MS, render
// and revalidate in background if < STALE_MS.
//
// Reads in this order:
//   1. In-memory map (instant, no JSON parse)
//   2. localStorage (parse + Date rehydration, ~1ms for typical
//      payloads). On hit, populates the in-memory map so a
//      subsequent read in the same session skips step 2.
export function getCachedView(key: string): { entry: CachedView; age: number } | null {
  const fromMem = cache.get(key)
  if (fromMem) {
    const age = Date.now() - fromMem.fetchedAt
    if (age > STALE_MS) {
      cache.delete(key)
      return null
    }
    return { entry: fromMem, age }
  }
  const fromLS = hydrateFromLS(key)
  if (!fromLS) return null
  const age = Date.now() - fromLS.fetchedAt
  if (age > STALE_MS) return null
  // Promote to in-memory so subsequent reads in this session are
  // free. Don't touch LRU here — it's only on writes, since reads
  // happen on every view-toggle and would thrash the LRU otherwise.
  cache.set(key, fromLS)
  return { entry: fromLS, age }
}

export function setCachedView(key: string, entry: Omit<CachedView, 'fetchedAt'>): void {
  const stamped: CachedView = { ...entry, fetchedAt: Date.now() }
  cache.set(key, stamped)
  // Mirror to localStorage, evicting the LRU tail if we'd exceed
  // LS_MAX_ENTRIES. Single key per tenant, single read+write —
  // keeps the cross-tab `storage` event simple.
  const blob = readPersisted() ?? {
    version: CACHE_VERSION,
    entries: {},
    lru: [],
  }
  blob.entries[key] = stamped
  blob.lru = [key, ...blob.lru.filter((k) => k !== key)]
  while (blob.lru.length > LS_MAX_ENTRIES) {
    const drop = blob.lru.pop()
    if (drop) delete blob.entries[drop]
  }
  writePersisted(blob)
  calendarCacheVersion.value++
}

// Drop everything from the cache. Used after a local mutation
// (drag-reschedule, click-to-create) so the next loadWeek refills
// from the server. Clears both memory and localStorage to keep
// them consistent — otherwise a refresh after a mutation would
// rehydrate stale data.
export function invalidateAll(): void {
  cache.clear()
  calendarCacheVersion.value++
  const key = lsKey()
  if (!key || typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(key)
  } catch {
    // Same fallthrough as writePersisted — silently skip.
  }
}

// Invalidate only the cached content belonging to specific external
// calendars, leaving every other calendar and all Stemne bookings in
// place. This is what `calendar.external.changed` should reach: the
// worker's delta job names the connection AND the calendars that moved,
// so throwing away all eight cached weeks — which is what invalidateAll
// does — spends the entire cache on a change to one calendar and turns
// the next render into a cold fetch behind a blank grid.
//
// Two things happen per cached view:
//
//  1. Events and banners from the named calendars are dropped. They are
//     the only content we KNOW is wrong, and keeping them would leave a
//     meeting the operator deleted in Google on screen. Everything else
//     is content we know is still good, so it keeps painting instantly.
//  2. EVERY view is aged just past FRESH_MS so the next read serves it
//     immediately AND revalidates in the background. Deliberately NOT
//     fetchedAt = 0: getCachedView DELETES anything older than
//     STALE_MS, so zeroing the stamp would destroy the entry on next
//     read and hand the operator the blank grid this function exists
//     to avoid.
//
// Step 2 has to be unconditional, and that is the subtle part. Dropping
// content only touches weeks that HAD content from the changed
// calendar — but the commonest change of all is an event being ADDED,
// and a week that gains its first event from that calendar has nothing
// to drop. Ageing only the views that lost something would leave that
// week's entry inside FRESH_MS, and loadWeek returns from a fresh
// entry without a network call at all, so the new meeting would never
// appear. The frame tells us the provider's data moved; it cannot tell
// us which weeks it moved in. So every cached view revalidates, and
// the ETag on GET /api/admin/calendar/events makes the ones that
// really were unaffected cost a 304.
//
// The ageing uses min() so it can only ever age an entry, never
// rejuvenate one: a view that was already 5 minutes old must not come
// out of an invalidation looking 30 seconds old.
//
// An empty id list means the sender could not scope the change, so we
// fall back to invalidateAll rather than guess.
export function invalidateCalendars(calendarIds: string[]): void {
  if (calendarIds.length === 0) {
    invalidateAll()
    return
  }
  const ids = new Set(calendarIds)
  const fromChangedCalendar = (x: unknown): boolean => {
    const id = (x as { calendar_id?: string })?.calendar_id
    return !!id && ids.has(id)
  }
  // Drop the changed calendars' content and mark the view for
  // background revalidation. Stemne bookings and every other
  // calendar's events survive, so the view still paints instantly.
  const stale = (view: CachedView): CachedView => ({
    ...view,
    events: view.events.filter((e) => !fromChangedCalendar(e)),
    allDayBanners: view.allDayBanners.filter((b) => !fromChangedCalendar(b)),
    fetchedAt: Math.min(view.fetchedAt, Date.now() - FRESH_MS - 1),
  })

  for (const [key, view] of cache) {
    cache.set(key, stale(view))
  }
  const blob = readPersisted()
  if (blob) {
    for (const key of Object.keys(blob.entries)) {
      blob.entries[key] = stale(blob.entries[key])
    }
    if (Object.keys(blob.entries).length > 0) writePersisted(blob)
  }
  calendarCacheVersion.value++
}

// Event kinds sourced from a connected external calendar provider, as
// opposed to 'stemne' (the operator's own bookings). The cached events
// array merges both; allDayBanners are exclusively external. Used by
// purgeExternalEvents to strip provider data on disconnect.
const EXTERNAL_KINDS = new Set(['google', 'outlook', 'apple'])

function notExternal(x: unknown): boolean {
  return !EXTERNAL_KINDS.has((x as { kind?: string })?.kind ?? '')
}

function stripExternal(view: CachedView): CachedView {
  return {
    ...view,
    events: view.events.filter(notExternal),
    allDayBanners: view.allDayBanners.filter(notExternal),
  }
}

// Remove only externally-sourced (Google/Outlook/Apple) events from
// every cached view — in memory and in localStorage — while leaving the
// operator's own Stemne bookings cached. Called after a calendar
// disconnect so provider event content (titles, times) doesn't linger
// in the browser once access is revoked, without throwing away the
// perf cache for native data.
//
// fetchedAt is left unchanged: the remaining Stemne events were valid
// as of that timestamp, and the next loadWeek revalidates on the normal
// staleness schedule. Drop-by-external-kind (not keep-only-stemne) is
// deliberate — an element with an unexpected/absent kind is preserved
// rather than silently dropped, so a shape drift can't lose native data.
export function purgeExternalEvents(): void {
  for (const [key, view] of cache) {
    cache.set(key, stripExternal(view))
  }
  calendarCacheVersion.value++
  const blob = readPersisted()
  if (!blob) return
  for (const key of Object.keys(blob.entries)) {
    blob.entries[key] = stripExternal(blob.entries[key])
  }
  writePersisted(blob)
}

// Subscribe to cross-tab cache writes. Fires when another tab
// calls setCachedView OR invalidateAll for this tenant. The
// callback runs after the in-memory cache has been refreshed
// from LS, so consumers can immediately rebuild their view.
//
// Returns a cleanup function. Caller is responsible for invoking
// it (typically in onBeforeUnmount).
export function onCrossTabUpdate(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const myLsKey = lsKey()
  if (!myLsKey) return () => {}
  function onStorage(e: StorageEvent) {
    if (e.key !== myLsKey) return
    // Drop the in-memory cache so subsequent getCachedView reads
    // pull the fresh blob from LS. Cheap; the view's next render
    // path will re-hydrate.
    cache.clear()
    cb()
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}

// Test/debug helper — exported so we can introspect from devtools
// without exposing the Map directly. Returns a snapshot, not a
// live reference.
export function _cacheSnapshot(): { size: number; keys: string[] } {
  return { size: cache.size, keys: Array.from(cache.keys()) }
}
